"use client";

import { createContext, use, useContext, useState, useEffect, useCallback, useMemo, useRef, useSyncExternalStore, type ReactNode } from "react";
import { preload } from "react-dom";
import { usePathname, useRouter } from "next/navigation";
import { type Locale, t, type TranslationKey } from "./i18n";
import { locales } from "./i18n-config";
import { registerMessages, setMissHandler, type Messages } from "./i18n/registry";
import { fileFor, fileUrl, namespaceFile, partsOf, routeSetFor } from "./i18n/routes";
import { claimAssetReload } from "./error-recovery";

interface LocaleContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  /** Warm the other locales' strings for the current page (a language
   *  menu calls it on open), so picking one swaps the text at once. */
  prefetchLocales: () => void;
  /** Bumps when strings fetched after a miss land, so consumers re-render. */
  version: number;
}

const LocaleContext = createContext<LocaleContextValue>({
  locale: "az",
  setLocale: () => {},
  prefetchLocales: () => {},
  version: 0,
});

// Translation loading. The server registers each locale's full map (SSR
// output is always complete). The browser loads only what the page renders
// (lib/i18n/routes.ts): the shared "core" file plus the route's own file,
// content-hashed static JSON written at prebuild
// (scripts/build-i18n-json.mjs) and preloaded with the HTML, so the
// requests run in parallel with the JS and are cached for good. The whole
// dictionary used to download on every page before it could hydrate
// (~80 KB gzipped, 2026-10-07).
//
// Dev imports the .ts module (fresh on edit) and keeps only the keys the
// generated file lists, so a manifest gap shows up in dev as a console
// warning (and is filled from the module at once) instead of in production.
// The .ts import is also the fallback when a JSON file cannot be fetched.
//
// Each promise carries React's thenable fields (status / value), so use()
// reads a loaded set synchronously instead of suspending again.
type Tracked = Promise<void> & { status?: "pending" | "fulfilled" | "rejected"; value?: void };

function tracked(p: Promise<void>): Tracked {
  const t = p as Tracked;
  t.status = "pending";
  t.then(() => { t.status = "fulfilled"; }, () => { t.status = "rejected"; });
  return t;
}

function settled(): Tracked {
  const done = Promise.resolve() as Tracked;
  done.status = "fulfilled";
  return done;
}

const isServer = typeof window === "undefined";
const isDev = process.env.NODE_ENV === "development";

async function importLocaleMap(locale: Locale): Promise<Messages> {
  switch (locale) {
    case "ru":
      return ((await import("./i18n/ru")) as { default: Messages }).default;
    case "en":
      return ((await import("./i18n/en")) as { default: Messages }).default;
    case "az":
    default:
      return ((await import("./i18n/az")) as { default: Messages }).default;
  }
}

// Dev only: the fresh .ts map per locale, the source of every dev file.
const devMaps: Partial<Record<Locale, Messages>> = {};

class I18nFileError extends Error {
  readonly status: number;
  readonly url: string;
  constructor(url: string, status: number) {
    super(`i18n ${status}`);
    this.status = status;
    this.url = url;
  }
}

async function fetchJson(url: string | undefined): Promise<Partial<Messages>> {
  if (!url) throw new Error("i18n: no generated file (run scripts/build-i18n-json.mjs)");
  const res = await fetch(url);
  if (!res.ok) throw new I18nFileError(url, res.status);
  return (await res.json()) as Partial<Messages>;
}

/** A translation file of this page's build that is gone (404): the page
 *  is from a build a deploy replaced, and the static archive did not keep
 *  the file. Reload into the current build once (claimAssetReload guards
 *  against a loop) and keep the strings pending until the page goes;
 *  false when the reload was already spent, then the full map stands in. */
function reloadForNewBuild(err: unknown): boolean {
  if (!(err instanceof I18nFileError) || err.status !== 404) return false;
  if (!claimAssetReload(err.url)) return false;
  window.location.reload();
  return true;
}

const files = new Map<string, Tracked>();

/** Load and register one generated file for a locale, once. */
function loadFile(locale: Locale, id: string): Tracked {
  const slot = `${locale}/${id}`;
  let p = files.get(slot);
  if (!p) {
    const namespace = id.startsWith("ns-") ? id.slice(3) : undefined;
    p = tracked((async () => {
      if (isDev) {
        const [full, listed] = await Promise.all([
          devMaps[locale] ? Promise.resolve(devMaps[locale]!) : importLocaleMap(locale),
          fetchJson(fileUrl(id, locale)).catch(() => null),
        ]);
        devMaps[locale] = full;
        if (!listed) {
          registerMessages(locale, full);
          return;
        }
        const pick: Partial<Messages> = {};
        for (const k of Object.keys(listed) as TranslationKey[]) pick[k] = full[k];
        registerMessages(locale, pick, { namespace });
        return;
      }
      try {
        registerMessages(locale, await fetchJson(fileUrl(id, locale)), { namespace });
      } catch (err) {
        if (reloadForNewBuild(err)) await new Promise<never>(() => {});
        registerMessages(locale, await importLocaleMap(locale));
      }
    })());
    // A combined file carries core and the route's own file: once it is
    // in, neither is fetched again.
    const parts = partsOf(id);
    if (parts) {
      void p.then(() => {
        for (const part of parts) if (!files.has(`${locale}/${part}`)) files.set(`${locale}/${part}`, settled());
      }, () => undefined);
    }
    files.set(slot, p);
  }
  return p;
}

const haveCore = (locale: Locale) => files.get(`${locale}/core`)?.status === "fulfilled";

const serverMaps: Partial<Record<Locale, Tracked>> = {};
const routes = new Map<string, Tracked>();

/**
 * Everything a page needs for a locale: the full map on the server, the
 * route's file in the browser. One thenable per (locale, route key set),
 * created once and reused for good: which file it fetches (combined or
 * route-only, see fileFor) is decided when it is created, so the key never
 * depends on load state. A key that did (core loaded or not) handed React
 * a new promise when it replayed the suspended render ("A component was
 * suspended by an uncached promise") and remounted pages (runtime audit,
 * 2026-10-08).
 */
function ensureStrings(locale: Locale, set: string | null): Tracked {
  if (isServer) {
    return (serverMaps[locale] ??= tracked(importLocaleMap(locale).then((m) => registerMessages(locale, m))));
  }
  const slot = `${locale}|${set ?? ""}`;
  let p = routes.get(slot);
  if (!p) {
    const file = loadFile(locale, fileFor(set, haveCore(locale)));
    p = file.status === "fulfilled" ? settled() : tracked(file.then(() => undefined));
    routes.set(slot, p);
  }
  return p;
}

// Strings fetched after a miss: a tiny store the provider subscribes to so
// every consumer re-renders once they land.
let missVersion = 0;
const missListeners = new Set<() => void>();
const subscribeMisses = (fn: () => void) => {
  missListeners.add(fn);
  return () => { missListeners.delete(fn); };
};
const missSnapshot = () => missVersion;
const missServerSnapshot = () => 0;
const warned = new Set<string>();

if (!isServer) {
  setMissHandler((locale, namespace, key) => {
    if (isDev) {
      const full = devMaps[locale];
      if (!warned.has(`${locale}/${namespace}`)) {
        warned.add(`${locale}/${namespace}`);
        console.warn(
          `[i18n] "${key}" is not in this page's translation files (${window.location.pathname}); `
          + `loading namespace "${namespace}". Re-run node scripts/build-i18n-json.mjs; if it persists, `
          + "the key is built in a way scripts/i18n-scan.mjs cannot see (start it with a literal prefix).",
        );
      }
      if (full) {
        const pick: Partial<Messages> = {};
        const prefix = namespace === "_" ? null : `${namespace}.`;
        for (const k of Object.keys(full) as TranslationKey[]) {
          if (prefix ? k.startsWith(prefix) : !k.includes(".")) pick[k] = full[k];
        }
        registerMessages(locale, pick, { namespace });
        return true;
      }
    }
    void loadFile(locale, namespaceFile(namespace)).then(() => {
      missVersion++;
      for (const fn of missListeners) fn();
    });
    return false;
  });
}

export function LocaleProvider({
  children,
  initialLocale,
}: {
  children: ReactNode;
  initialLocale?: Locale;
}) {
  // The page's strings must be registered BEFORE children render and read
  // `t`, on the server and in the browser alike, so SSR output and the
  // first client render match. Until they are in, this suspends (use):
  // during hydration React keeps the server HTML on screen and hydrates
  // once the preloaded files have arrived; on a client navigation to a
  // page whose files are not loaded yet, the router's transition keeps the
  // old page up meanwhile (links warm them on hover/touch, below).
  //
  // use() is called on every render, loaded or not: when the first render
  // suspends here, React replays this component and relies on the same
  // use() call to switch to its mount dispatcher. Skipping it once the
  // map had arrived made the next useState throw "Update hook called on
  // initial render" (intermittent page errors, runtime audit 2026-10-07).
  const [locale, setLocaleState] = useState<Locale>(initialLocale ?? "az");
  const [mounted, setMounted] = useState(!!initialLocale);
  const pathname = usePathname();
  const router = useRouter();
  const set = routeSetFor(pathname || "/");
  if (initialLocale) {
    if (isServer) {
      // Start the browser's download with the HTML instead of after the JS,
      // at low priority so it does not take bandwidth from the CSS, fonts
      // and LCP image on a slow phone connection. The browser's first
      // render asks for the same (combined) file.
      const href = fileUrl(fileFor(set, false), locale);
      if (href) preload(href, { as: "fetch", crossOrigin: "anonymous", fetchPriority: "low" });
    }
    use(ensureStrings(locale, set) as Promise<void>);
  }
  const version = useSyncExternalStore(subscribeMisses, missSnapshot, missServerSnapshot);

  // setLocale (and the link warmer below) read the live locale and path
  // through refs so they keep one identity for the provider's life:
  // depending on them made the context value change on every navigation
  // and re-rendered every consumer.
  const localeRef = useRef(locale);
  localeRef.current = locale;
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  useEffect(() => {
    if (!initialLocale) {
      // Legacy fallback: no URL locale prop, so the active map was not passed
      // in. Read the saved locale, load its map on the client, then flip state
      // and reveal - keeping `mounted` false until the strings are present so
      // the gated render below never flashes raw keys.
      const saved = localStorage.getItem("bina-locale") as Locale | null;
      const target: Locale =
        saved && (locales as readonly string[]).includes(saved) ? saved : "az";
      let cancelled = false;
      void ensureStrings(target, routeSetFor(window.location.pathname)).then(() => {
        if (cancelled) return;
        setLocaleState(target);
        setMounted(true);
      });
      return () => {
        cancelled = true;
      };
    }
    setMounted(true);
  }, [initialLocale]);

  // Warm the strings of a page the visitor is about to open: a link under
  // the pointer, focus or finger. Navigating there then never waits on
  // them (the route's file is a few KB and cached for good).
  useEffect(() => {
    let last = "";
    const warm = (e: Event) => {
      const a = (e.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!a || a.origin !== window.location.origin || a.pathname === last) return;
      last = a.pathname;
      void ensureStrings(localeRef.current, routeSetFor(a.pathname));
    };
    const opts = { passive: true, capture: true } as const;
    document.addEventListener("pointerover", warm, opts);
    document.addEventListener("touchstart", warm, opts);
    document.addEventListener("focusin", warm, opts);
    return () => {
      document.removeEventListener("pointerover", warm, opts);
      document.removeEventListener("touchstart", warm, opts);
      document.removeEventListener("focusin", warm, opts);
    };
  }, []);

  // Browser back/forward into a stale URL with the *previous* locale
  // prefix needs to redirect to the user's currently-chosen locale.
  // The earlier router.replace fix (#167) only updated the current
  // history entry; previous entries still had the old prefix, so
  // back navigation lost the locale.
  //
  // Strategy: when the user explicitly picks a locale via the
  // toggle, set sessionStorage flag ``locale-chosen``. On every
  // [locale]/layout mount thereafter, if the URL prefix differs
  // from the stored locale AND the chosen flag is set, redirect
  // via router.replace. The flag scopes the behaviour to "user
  // who made an explicit choice in this tab" - direct/shared-link
  // visits in fresh tabs are not hijacked.
  //
  // initialLocale is the URL-derived locale, so this hook runs on
  // every [locale] segment change (including back navigation that
  // remounts the layout with a different segment).
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!initialLocale) return;
    let chosen: string | null = null;
    try { chosen = sessionStorage.getItem("locale-chosen"); } catch { /* ignore */ }
    if (!chosen) return;
    let stored: Locale | null = null;
    try {
      const raw = localStorage.getItem("bina-locale");
      if (raw && (locales as readonly string[]).includes(raw)) stored = raw as Locale;
    } catch { /* ignore */ }
    if (!stored || stored === initialLocale) return;
    // URL has a stale locale prefix - redirect to the chosen one.
    const segments = window.location.pathname.split("/");
    if (!(locales as readonly string[]).includes(segments[1])) return;
    segments[1] = stored;
    const newPath = segments.join("/") || "/";
    const suffix = window.location.search + window.location.hash;
    router.replace(newPath + suffix, { scroll: false });
  }, [initialLocale, router]);

  const setLocale = useCallback((newLocale: Locale) => {
    const from = localeRef.current;
    const pathname = pathnameRef.current;
    // Update text immediately on the current page (no wait for navigation)
    // Fire-and-forget event BEFORE the state update so the `from`
    // value still reflects the previous locale.
    try {
      // Dynamic import so the tracker bundle isn't required at SSR
      // and the locale provider stays tree-shakeable. The Promise
      // is intentionally not awaited - the toggle reacts instantly.
      void import("@/lib/tracking").then(({ trackEvent }) => {
        trackEvent("locale.change", { from, to: newLocale });
      });
    } catch {
      /* never let telemetry block the toggle */
    }
    // Flip the visible locale only AFTER the target strings are registered,
    // so the "instant text update on the current page" never renders blanks
    // for a not-yet-loaded locale. Opening the language menu prefetches them
    // (prefetchLocales), so by the time a locale is picked this is already
    // settled and the swap happens in the same frame; a cold switch waits on
    // one or two small files (tens of ms).
    const ready = ensureStrings(newLocale, routeSetFor(pathname));
    if (ready.status === "fulfilled") {
      setLocaleState(newLocale);
    } else {
      void ready.then(() => setLocaleState(newLocale));
    }
    localStorage.setItem("bina-locale", newLocale);
    // Per-tab "user explicitly picked a locale" signal. The mount
    // reconciliation hook above only fires when this flag is set,
    // so direct/shared-link visits to a different-locale URL aren't
    // hijacked - only in-app back navigation by users who chose.
    try { sessionStorage.setItem("locale-chosen", "1"); } catch { /* ignore */ }
    document.cookie = `NEXT_LOCALE=${newLocale};path=/;max-age=${365 * 24 * 60 * 60};SameSite=Lax`;
    document.documentElement.lang = newLocale;
    // Swap the locale prefix via Next.js's router so the router's
    // internal stack stays in sync with the browser URL. Previously
    // we used window.history.replaceState directly - that updated the
    // URL bar but left Next.js's router still pointing at the old
    // /en/ entry. When the user then navigated forward and pressed
    // back, the router restored the stale /en/ entry and the page
    // re-rendered in English even though the URL had been "Russian"
    // for the whole flow. router.replace(...) goes through the App
    // Router's history, so back navigation correctly lands on the
    // /ru/ entry.
    //
    // usePathname() drops the query string and hash, so pull those
    // from window.location to preserve things like ?id=140 on
    // /[locale]/h.
    const segments = pathname.split("/");
    if (locales.includes(segments[1] as Locale)) {
      segments[1] = newLocale;
    } else {
      segments.splice(1, 0, newLocale);
    }
    const newPath = segments.join("/") || "/";
    const suffix = window.location.search + window.location.hash;
    router.replace(newPath + suffix, { scroll: false });
  }, [router]);

  const prefetchLocales = useCallback(() => {
    for (const l of locales) {
      if (l !== localeRef.current) void ensureStrings(l, routeSetFor(pathnameRef.current || "/"));
    }
  }, []);

  const value = useMemo(
    () => ({ locale, setLocale, prefetchLocales, version }),
    [locale, setLocale, prefetchLocales, version],
  );

  // Don't render until we've read localStorage to prevent flash
  if (!mounted) {
    return <div className="min-h-screen" />;
  }

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  return useContext(LocaleContext);
}

/** Convenience hook: returns a translator function bound to current locale.
 *  Stable per locale: a fresh function on every render made every effect
 *  that lists `t` in its deps re-run on each render, and the home
 *  "recent work" card re-fetched /api/history in an endless loop
 *  (owner, 2026-10-07). A new function only when the locale changes or
 *  strings fetched after a miss land. */
export function useT() {
  const { locale, version } = useLocale();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- version: strings fetched after a miss
  return useCallback((key: TranslationKey) => t(key, locale), [locale, version]);
}

// Re-export for convenience
export type { Locale, TranslationKey };
